import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import '../../styles/components/admin/AdminProfileSettings.css';

import logoutIcon from '../../assets/icons/indicators/logout_admin_icon.svg';
import accountIcon from '../../assets/icons/indicators/change_admin_icon.svg';
import systemLogo from '../../assets/images/Neurolex_Logo_New.png'; 
import eyeIcon from '../../assets/icons/indicators/eye_open_icon.svg';
import eyeOffIcon from '../../assets/icons/indicators/eye_closed_icon.svg';

// Import icons
//import eyeIcon from '../../../assets/icons/eye_icon.svg'; // You may need to add this
//import eyeOffIcon from '../../../assets/icons/eye_off_icon.svg'; // You may need to add this

const AdminProfileSettings = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Form states
  const [emailForm, setEmailForm] = useState({
    currentEmail: '',
    newEmail: '',
    password: ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Admin registration form state
  const [adminForm, setAdminForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // UI states
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    adminNew: false,
    adminConfirm: false
  });
  
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');

  // Load user data when component mounts
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);
      setEmailForm(prev => ({
        ...prev,
        currentEmail: userData.email || ''
      }));
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');

    // Validation
    const newErrors = {};
    
    if (!emailForm.newEmail) {
      newErrors.newEmail = 'New email is required';
    } else if (!validateEmail(emailForm.newEmail)) {
      newErrors.newEmail = 'Please enter a valid email address';
    } else if (emailForm.newEmail === emailForm.currentEmail) {
      newErrors.newEmail = 'New email must be different from current email';
    }
    
    if (!emailForm.password) {
      newErrors.password = 'Current password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      
      // Call API to update email
      const response = await authService.updateEmail({
        newEmail: emailForm.newEmail,
        currentPassword: emailForm.password
      });

      if (response.success) {
        // Update user data in localStorage
        const updatedUser = { ...user, email: emailForm.newEmail };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setEmailForm(prev => ({
          ...prev,
          currentEmail: emailForm.newEmail,
          newEmail: '',
          password: ''
        }));
        setSuccess('Email updated successfully!');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Failed to update email. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');

    // Validation
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!validatePassword(passwordForm.newPassword)) {
      newErrors.newPassword = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      
      // Call API to update password
      const response = await authService.updatePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      });

      if (response.success) {
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSuccess('Password updated successfully!');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Failed to update password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    try {
      authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Force logout even if there's an error
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      window.location.href = '/login';
    }
    setShowLogoutModal(false);
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');

    // Validation
    const newErrors = {};
    
    if (!adminForm.firstName.trim()) {
      newErrors.adminFirstName = 'First name is required';
    }
    
    if (!adminForm.lastName.trim()) {
      newErrors.adminLastName = 'Last name is required';
    }
    
    if (!adminForm.email) {
      newErrors.adminEmail = 'Email is required';
    } else if (!validateEmail(adminForm.email)) {
      newErrors.adminEmail = 'Please enter a valid email address';
    }
    
    if (!adminForm.password) {
      newErrors.adminPassword = 'Password is required';
    } else if (!validatePassword(adminForm.password)) {
      newErrors.adminPassword = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!adminForm.confirmPassword) {
      newErrors.adminConfirmPassword = 'Please confirm your password';
    } else if (adminForm.password !== adminForm.confirmPassword) {
      newErrors.adminConfirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      
      // Call the admin registration API
      const response = await authService.createAdminUser({
        firstName: adminForm.firstName.trim(),
        lastName: adminForm.lastName.trim(),
        email: adminForm.email.toLowerCase().trim(),
        password: adminForm.password
      });

      if (response.success) {
        setSuccess(`Admin account created successfully for ${adminForm.email}`);
        setAdminForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error creating admin account:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Failed to create admin account. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
          <div className="admin-profile-settings-wrapper">
            <div className="admin-profile-header-section">
              <div>
                <h1>My Admin Profile</h1>
                <p>Keep your information and availability up to date.</p>
              </div>
            </div>

            <div className="admin-profile-main-container">
              {/* Left Sidebar - Tab Navigation */}
              <div className="admin-profile-tabs-sidebar">
          <div className="admin-profile-tabs-container">
            <button
              className={`admin-profile-tab-button ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              <img src={accountIcon} alt="Account" className="admin-profile-tab-icon" />
              Account Settings
            </button>
            <button
              className={`admin-profile-tab-button ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <img src={accountIcon} alt="Account" className="admin-profile-tab-icon" />
              Security
            </button>
            {user.role === 'admin' && (
              <button
                className={`admin-profile-tab-button ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => setActiveTab('admin')}
              >
                Admin Management
              </button>
            )}
          
          
          {/* Logout Button moved to sidebar */}
          <button
            onClick={handleLogout}
            className="admin-profile-sidebar-logout"
          >
            <img src={logoutIcon} alt="Logout" className="admin-profile-logout-icon" />
            Logout
          </button>
          </div>
        </div>

      {/* Right Content Area */}
      <div className="admin-profile-content-area">
        {/* Success/Error Messages */}
        {success && (
          <div className="admin-profile-alert admin-profile-alert-success">
            {success}
          </div>
        )}
        
        {errors.general && (
          <div className="admin-profile-alert admin-profile-alert-error">
            {errors.general}
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'account' && (
          <div className="account-settings">
            <div className="admin-profile-settings-section">
              <h2>Change Email Address</h2>
            </div>

            <div className="admin-profile-settings-section">
              <form onSubmit={handleEmailSubmit} className="admin-profile-settings-form">
                <div className="admin-profile-form-group">
                  <label htmlFor="currentEmail">Current Email</label>
                  <input
                    type="email"
                    id="currentEmail"
                    value={emailForm.currentEmail}
                    disabled
                    className="admin-profile-form-input disabled"
                  />
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="newEmail">New Email Address</label>
                  <input
                    type="email"
                    id="newEmail"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                    className={`admin-profile-form-input ${errors.newEmail ? 'error' : ''}`}
                    placeholder="Enter new email address"
                  />
                  {errors.newEmail && <span className="admin-profile-error-text">{errors.newEmail}</span>}
                </div>

                <div className="admin-profile-form-group">
                    <label htmlFor="emailPassword">Current Password</label>
                    <div className="admin-profile-password-wrapper">
                      <input
                        type={showPasswords.emailPassword ? 'text' : 'password'}
                        id="emailPassword"
                        value={emailForm.password}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                        className={`admin-profile-form-input ${errors.password ? 'error' : ''}`}
                        placeholder="Enter current password to confirm"
                      />
                      <button
                        type="button"
                        className="admin-profile-password-toggle"
                        onClick={() => togglePasswordVisibility('emailPassword')}
                      >
                        <img 
                          src={showPasswords.emailPassword ? eyeIcon : eyeOffIcon} 
                          alt={showPasswords.emailPassword ? 'Hide password' : 'Show password'}
                          className="admin-profile-eye-icon"
                        />
                      </button>
                    </div>
                    {errors.password && <span className="admin-profile-error-text">{errors.password}</span>}
                  </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="admin-profile-btn admin-profile-btn-primary"
                >
                  {loading ? 'Updating...' : 'Update Email'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="security-settings">
            <div className="admin-profile-settings-section">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordSubmit} className="admin-profile-settings-form">
                <div className="admin-profile-form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <div className="admin-profile-password-wrapper">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      id="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className={`admin-profile-form-input ${errors.currentPassword ? 'error' : ''}`}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('current')}
                    >
                      <img 
                          src={showPasswords.current ? eyeIcon : eyeOffIcon} 
                          alt={showPasswords.current ? 'Hide password' : 'Show password'}
                          className="admin-profile-eye-icon"
                        />
                    </button>
                  </div>
                  {errors.currentPassword && <span className="admin-profile-error-text">{errors.currentPassword}</span>}
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="admin-profile-password-wrapper">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      id="newPassword"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      className={`admin-profile-form-input ${errors.newPassword ? 'error' : ''}`}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('new')}
                    >
                      <img 
                        src={showPasswords.new ? eyeIcon : eyeOffIcon} 
                        alt={showPasswords.new ? 'Hide password' : 'Show password'}
                        className="admin-profile-eye-icon"
                      />
                    </button>
                  </div>
                  {errors.newPassword && <span className="admin-profile-error-text">{errors.newPassword}</span>}
                  <small className="admin-profile-password-help">
                    Password must be at least 8 characters with uppercase, lowercase, and number
                  </small>
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="admin-profile-password-wrapper">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      id="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className={`admin-profile-form-input ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('confirm')}
                    >
                      <img 
                          src={showPasswords.confirm ? eyeIcon : eyeOffIcon} 
                          alt={showPasswords.confirm ? 'Hide password' : 'Show password'}
                          className="admin-profile-eye-icon"
                        />
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="admin-profile-error-text">{errors.confirmPassword}</span>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="admin-profile-btn admin-profile-btn-primary"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="admin-profile-admin-management">
            <div className="admin-profile-settings-section">
              <h2>Create New Admin Account</h2>
              
              <form onSubmit={handleAdminSubmit} className="admin-profile-settings-form">
                <div className="admin-profile-form-row">
                  <div className="admin-profile-form-group">
                    <label htmlFor="adminFirstName">First Name</label>
                    <input
                      type="text"
                      id="adminFirstName"
                      value={adminForm.firstName}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className={`admin-profile-form-input ${errors.adminFirstName ? 'error' : ''}`}
                      placeholder="Enter first name"
                    />
                    {errors.adminFirstName && <span className="admin-profile-error-text">{errors.adminFirstName}</span>}
                  </div>

                  <div className="admin-profile-form-group">
                    <label htmlFor="adminLastName">Last Name</label>
                    <input
                      type="text"
                      id="adminLastName"
                      value={adminForm.lastName}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className={`admin-profile-form-input ${errors.adminLastName ? 'error' : ''}`}
                      placeholder="Enter last name"
                    />
                    {errors.adminLastName && <span className="admin-profile-error-text">{errors.adminLastName}</span>}
                  </div>
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="adminEmail">Email Address</label>
                  <input
                    type="email"
                    id="adminEmail"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                    className={`admin-profile-form-input ${errors.adminEmail ? 'error' : ''}`}
                    placeholder="Enter email address"
                  />
                  {errors.adminEmail && <span className="admin-profile-error-text">{errors.adminEmail}</span>}
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="adminPassword">Password</label>
                  <div className="admin-profile-password-wrapper">
                    <input
                      type={showPasswords.adminNew ? 'text' : 'password'}
                      id="adminPassword"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                      className={`admin-profile-form-input ${errors.adminPassword ? 'error' : ''}`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('adminNew')}
                    >
                      <img 
                          src={showPasswords.adminNew ? eyeIcon : eyeOffIcon} 
                          alt={showPasswords.adminNew ? 'Hide password' : 'Show password'}
                          className="admin-profile-eye-icon"
                        />
                    </button>
                  </div>
                  {errors.adminPassword && <span className="admin-profile-error-text">{errors.adminPassword}</span>}
                  <small className="admin-profile-password-help">
                    Password must be at least 8 characters with uppercase, lowercase, and number
                  </small>
                </div>

                <div className="admin-profile-form-group">
                  <label htmlFor="adminConfirmPassword">Confirm Password</label>
                  <div className="admin-profile-password-wrapper">
                    <input
                      type={showPasswords.adminConfirm ? 'text' : 'password'}
                      id="adminConfirmPassword"
                      value={adminForm.confirmPassword}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className={`admin-profile-form-input ${errors.adminConfirmPassword ? 'error' : ''}`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('adminConfirm')}
                    >
                      <img 
                          src={showPasswords.adminConfirm ? eyeIcon : eyeOffIcon} 
                          alt={showPasswords.adminConfirm ? 'Hide password' : 'Show password'}
                          className="admin-profile-eye-icon"
                        />
                    </button>
                  </div>
                  {errors.adminConfirmPassword && <span className="admin-profile-error-text">{errors.adminConfirmPassword}</span>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="admin-profile-btn admin-profile-btn-primary"
                >
                  {loading ? 'Creating Admin Account...' : 'Create Admin Account'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="admin-profile-logout-overlay">
          <div className="admin-profile-logout-modal">
            <div className="admin-profile-logout-modal-header">
              <img src={systemLogo} alt="System Logo" className="admin-profile-logout-logo" />
              <h2 className="admin-profile-logout-system-name">LOGOUT OF ACCOUNT?</h2>
            </div>
            
            <div className="admin-profile-logout-admin-info">
              <div className="admin-profile-logout-admin-label">Neurolex Administrator</div>
              <div className="admin-profile-logout-admin-email">{user.email || 'admin@example.com'}</div>
            </div>
            
            <p className="admin-profile-logout-message">
              You will be signed out of your account and will need to log in again to continue.
            </p>
            
            <div className="admin-profile-logout-actions">
              <button 
                className="admin-profile-logout-cancel"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>
              <button 
                className="admin-profile-logout-confirm"
                onClick={confirmLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
  </div>
);
};

export default AdminProfileSettings;